type RuleBuilder = {
  required: () => RuleBuilder;
  minLength: (value: number) => RuleBuilder;
  maxLength: (value: number) => RuleBuilder;
  min: (value: number) => RuleBuilder;
  max: (value: number) => RuleBuilder;
  pattern: (regex: RegExp, message?: string) => RuleBuilder;
  slug: () => RuleBuilder;
  url: () => RuleBuilder;
  domain: () => RuleBuilder;
  file: (allowedTypes: string[]) => RuleBuilder;
  fromType: (type: string) => RuleBuilder;
  get: () => Record<string, any>;
};

export const useFieldRules = () => {
  return (label: string): RuleBuilder => {
    const rules: Record<string, any> = {};

    const builder: RuleBuilder = {
      required: () => {
        rules.required = `${label} is required`;
        return builder;
      },
      minLength: (value: number) => {
        rules.minLength = {
          value,
          message: `${label} must be at least ${value} characters`,
        };
        return builder;
      },
      maxLength: (value: number) => {
        rules.maxLength = {
          value,
          message: `${label} must be at most ${value} characters`,
        };
        return builder;
      },
      min: (value: number) => {
        rules.min = {
          value,
          message: `${label} must be at least ${value}`,
        };
        return builder;
      },
      max: (value: number) => {
        rules.max = {
          value,
          message: `${label} must be at most ${value}`,
        };
        return builder;
      },
      pattern: (regex: RegExp, message?: string) => {
        rules.pattern = {
          value: regex,
          message: message || `${label} is invalid`,
        };
        return builder;
      },
      slug: () => {
        rules.pattern = {
          value: /^[a-z0-9-]+$/,
          message: `${label} must only contain lowercase letters, numbers, and dashes`,
        };
        return builder;
      },
      url: () => {
        rules.pattern = {
          value:
            /^(https?:\/\/)([^\s.]+\.[^\s]{2,}|localhost)(:\d+)?(\/[^\s]*)?$/i,
          message: `${label} must be a valid URL`,
        };
        return builder;
      },
      domain: () => {
        rules.validate = (value: string) => {
          if (/^https?:\/\//i.test(value)) {
            return `${label} must not include http or https`;
          }

          if (!/^([a-z0-9-]+\.)+[a-z]{2,}$/i.test(value)) {
            return `${label} must be a valid domain (e.g. example.com)`;
          }

          return true;
        };
        return builder;
      },
      file: (allowedTypes: string[]) => {
        rules.validate = (value: FileList | null) => {
          if (!value || value.length === 0) {
            return true;
          }

          const file = value[0];
          const ext = file.name.split(".").pop()?.toLowerCase();

          const allowed = allowedTypes.map((t) => t.toLowerCase());

          if (!ext || !allowed.includes(ext)) {
            return `${label} must be a ${allowed.join(", ")} file`;
          }

          return true;
        };
        return builder;
      },
      fromType: (type: string) => {
        switch (type) {
          case "string":
            builder.required().minLength(1);
            break;
          case "integer":
            builder.required();
            rules.valueAsNumber = true;
            rules.validate = (value: any) =>
              !isNaN(Number(value)) || `${label} must be a valid number`;
            break;
          case "boolean":
            builder.required();
            rules.validate = (value: any) => {
              const valid =
                value === 0 || value === 1 || value === "0" || value === "1";
              return valid || `${label} must be 0 or 1`;
            };
            break;
          default:
            builder.required();
        }
        return builder;
      },
      get: () => rules,
    };

    return builder;
  };
};
